import Popup from "../../lib/Popup";

import * as PIXI from "pixi.js";
import gsap from "gsap";
import { IGameSnacks } from "../../lib/GameSnacks";
import ServiceLocator from "../../ServiceLocator";
import Button from "../../lib/Button";
import { Profile } from "../../lib/game_snacks/IUser";
import Session, { game_type } from "../../Session";
import { LiveEventData, LiveEventDataType, LivePlayerDataEvent, LivePresenceAction } from "../../lib/game_snacks/ILiveMultiplayer";
import { Signal } from "signals";
import PRNG from "../../lib/PRNG";
import PlayerSlot from "./PlayerSlot";
import { game_event_type } from "../../service/MultiplayerEventManager";
import Settings from "../Settings";
import TutorialPopup from "./TutorialPopup";

declare global {
    interface Window { GameSnacks: IGameSnacks; }
}


export default class HostPrivateRoomPopup extends Popup
{
    // #region Attributes //

    // private:

    private _url: string = null;

    private _gameType: game_type = null;

    private _classicModeBtn: Button = null;
    private _pointsModeBtn: Button = null;
    private _gameStartedBg: PIXI.Sprite = null;
    private _versus: PIXI.Sprite = null;
    private _versusExplosion: PIXI.Sprite = null;
    private _headerText: PIXI.Text = null;
    private _footerText: PIXI.Text = null;
    private _arrPlayerSlot: Array<PlayerSlot> = null;
    private _settings: Settings = null;
    private _backBtn: Button = null;

    private _isGameStarted: boolean = null;
    private _isPlayAgain: boolean = null;

    private _startBtn: Button = null;

    private _copiedUrlNotification: PIXI.Container = null;

    private _gameReadyTl: gsap.core.Timeline = null;
    private _copiedUrlNotificationTl: gsap.core.Timeline = null;

    private _onGameStarted: Signal = null;

    // #endregion //


    public set url( value: string ) { this._url = value; }
    public set gameType( value: game_type ) { this._gameType = value; }

    // Signals.
    public get onGameStarted(): Signal { return this._onGameStarted; } 


    // #region Methods //

    // public:

    public onEnter(): void
    {
        super.onEnter();        

        Session.i.privateRoomUrl = this._url;

        Session.i.profile.dartSkinIndex = ServiceLocator.saved.data.selDartIndex;

        this._onGameStarted = new Signal();

        this._arrPlayerSlot = [];
        this._isGameStarted = false;
        this._isPlayAgain = Session.i.room.length > 1;
        if ( this._gameType === null )
        {
            this._gameType = game_type.CLASSIC_PRIVATE;
        }

        let bg: PIXI.Sprite = new PIXI.Sprite( ServiceLocator.resources.matchmaking_background_01.texture );
        bg.anchor.set( 0.5 );
        bg.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._root.addChild( bg );

        this._gameStartedBg = new PIXI.Sprite( ServiceLocator.resources.matchmaking_background_02.texture );
        this._gameStartedBg.visible = false;
        this._gameStartedBg.anchor.set( 0.5 );
        this._gameStartedBg.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._root.addChild( this._gameStartedBg );

        this._versusExplosion = new PIXI.Sprite( ServiceLocator.resources.vs_background_effect.texture );
        this._versusExplosion.visible = false;
        this._versusExplosion.anchor.set( 0.5 );
        this._versusExplosion.position.set( ServiceLocator.app.width * 0.5, 619 );
        this._root.addChild( this._versusExplosion );

        this._headerText = new PIXI.Text( "Play With Friends", { fontFamily: "jaro_regular", fontSize: 80, fill: 0xffffff, stroke: 0x000000, strokeThickness: 12, letterSpacing: 2 } );
        this._headerText.anchor.set( 0.5 );
        this._headerText.position.set( ServiceLocator.app.width * 0.5, 158 );
        this._root.addChild( this._headerText );

        let gameModeText: PIXI.Text = new PIXI.Text( "Selected game mode:", { fontFamily: "jaro_regular", fontSize: 40, fill: 0xffffff } );
        gameModeText.anchor.set( 0.5 );
        gameModeText.position.set( ServiceLocator.app.width * 0.5, 270 );
        this._root.addChild( gameModeText );

        this._classicModeBtn = new Button( { on: "button_mode_classic", pressed: "button_mode_classic_pressed" } );
        this._classicModeBtn.interactive = false;
        this._classicModeBtn.anchor.set( 0.5 );
        this._classicModeBtn.position.set( 217, 371 );
        this._root.addChild( this._classicModeBtn );
        this._classicModeBtn.on( "pointertap", this._onClassicModeBtn_Click, this );

        this._pointsModeBtn = new Button( { on: "button_mode_301_off", pressed: "button_mode_301_pressed" } );
        this._pointsModeBtn.anchor.set( 0.5 );
        this._pointsModeBtn.position.set( 421, 371 );
        this._root.addChild( this._pointsModeBtn );
        this._pointsModeBtn.on( "pointertap", this._onPointsModeBtn_Click, this );

        const kArrPos: Array<PIXI.Point> = [ 
            new PIXI.Point( 158, 614 ),
            new PIXI.Point( ServiceLocator.app.width - 158, 614 ) ];
        for ( let i: number = 0; i < Session.i.kGameRoomSize; ++i )
        {
            let playerSlot: PlayerSlot = new PlayerSlot( ServiceLocator.resources.matchmaking_avatar_base.texture );
            playerSlot.position.copyFrom( kArrPos[ i ] );
            this._root.addChild( playerSlot );            
            //
            this._arrPlayerSlot.push( playerSlot );
        }
        this._arrPlayerSlot[ 0 ].setPlayer( Session.i.profile );
        //
        if ( this._isPlayAgain )
        {
            this._updateOtherPlayerSlots();
        }

        this._versus = new PIXI.Sprite( ServiceLocator.resources.vs.texture );
        this._versus.visible = false;
        this._versus.anchor.set( 0.5 );
        this._versus.position.set( ServiceLocator.app.width * 0.5 + 8, 605 );
        this._root.addChild( this._versus );

        this._backBtn = new Button( { on: "button_back", pressed: "button_back_pressed" } );
        this._backBtn.anchor.set( 0.5 );
        this._backBtn.position.set( 48, 44 + 16 );
        this._root.addChild( this._backBtn );
        this._backBtn.on( "pointertap", () => { 
            this._root.interactiveChildren = false;
            if ( window.GameSnacks )
            {
                window.GameSnacks.liveMultiplayer.updatePlayerPresence( LivePresenceAction.LEAVE )
                    .then( () => {
                        this._onClosed.dispatch( this ); 
                    } );
            }
            else
            {
                this._onClosed.dispatch( this ); 
            }
        } );

        this._footerText = new PIXI.Text( "", { fontFamily: "jaro_regular", fontSize: 40, fill: 0xffffff } );
        this._footerText.anchor.set( 0.5 );
        this._footerText.position.set( ServiceLocator.app.width * 0.5, 900 );
        this._root.addChild( this._footerText );
        if ( this._isPlayAgain )
        {
            if ( !Session.i.room[ 1 ].isReady )
            {
                this._footerText.text = "Waiting for " + Session.i.room[ 1 ].username.prefix + " to play again..."
            }
            else
            {
                this._footerText.text = Session.i.room[ 1 ].username.prefix + " just joined! Let's Play!"
            }
        }

        this._settings = new Settings();
        this._settings.init( true );
        this._settings.position.set( 592, 7 + 16 );
        this._root.addChild( this._settings );
        this._settings.onTutorial.add( () => { 
            ServiceLocator.app.hidePrivateGameShareUrlDiv();

            let tutorialPopup: TutorialPopup = new TutorialPopup();
            tutorialPopup.init();
            this._root.addChild( tutorialPopup );
            tutorialPopup.onClosed.add( ( popup: TutorialPopup ) => {
                popup.end();

                if ( !this._isPlayAgain || this._isPlayAgain && Session.i.room.length == 1 )
                {
                    ServiceLocator.app.showPrivateGameShareUrlDiv( this._url );
                }
            } );
        } );
        this._settings.onHome.addOnce( () => { 
            this._root.interactiveChildren = false;
            window.GameSnacks.liveMultiplayer.updatePlayerPresence( LivePresenceAction.LEAVE )
                .then( () => {
                    this._root.interactiveChildren = true;

                    this._onClosed.dispatch( this ); 
                } );
        } );

        this._startBtn = new Button( { on: "button_play", pressed: "button_play_pressed" } );
        this._startBtn.anchor.set( 0.5 );
        this._startBtn.position.set( ServiceLocator.app.width * 0.5, 996 + 44 );
        this._root.addChild( this._startBtn );
        this._startBtn.on( "pointertap", this._onStartBtn_Click, this );
        this._startBtn.visible = false;
        if ( this._isPlayAgain && Session.i.room[ 1 ].isReady )
        {
            this._startBtn.visible = true;
        }

        this._copiedUrlNotification = new PIXI.Container();
        this._copiedUrlNotification.visible = false;
        this._copiedUrlNotification.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
        this._root.addChild( this._copiedUrlNotification );
        //
        let notificationBase: PIXI.Graphics = new PIXI.Graphics();
        notificationBase.beginFill( 0xbff44d, 0.9 );
        const kHalfWidth: number = 200;
        const kHalfHeight: number = 40;
        notificationBase.drawPolygon( 
            -kHalfWidth, -kHalfHeight, 
            kHalfWidth, -kHalfHeight + 6, 
            kHalfWidth - 6, kHalfHeight,
            -kHalfWidth + 10, kHalfHeight - 4 );
        notificationBase.endFill();
        this._copiedUrlNotification.addChild( notificationBase );
        //
        let notificationText: PIXI.Text = new PIXI.Text( "Link Copied!", { 
            fontSize: 50, 
            fontFamily: "college_tm_regular", 
            fill: 0x192f62, 
            stroke: 0xffffff, 
            strokeThickness: 4,
            dropShadow: true,
            dropShadowAngle: 2,
            dropShadowColor: "#ffffff",
            dropShadowDistance: 2,
            lineJoin: "round" } );
        notificationText.anchor.set( 0.5 );
        this._copiedUrlNotification.addChild( notificationText );

        if ( !this._isPlayAgain )
        {
            ServiceLocator.app.showPrivateGameShareUrlDiv( this._url );
        }
        ServiceLocator.app.onCopyUrlBtnClick.add( this._onCopyUrlBtn_Click, this );

        ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.add( this._onMultiplayer_PlayerPresenceUpdated, this );
        ServiceLocator.multiplayerEventManager.onEventsReceived.add( this._onMultiplayer_EventsReceived, this );
        ServiceLocator.multiplayerEventManager.onProfileChange.add( this._onProfile_Changed, this );
    }

        private _onCopyUrlBtn_Click(): void
        {
            if ( this._root.interactiveChildren )
            {
                this._root.interactiveChildren = false;

                window.GameSnacks.liveMultiplayer.getRoomUrl()
                    .finally( () => { 
                        this._copiedUrlNotification.position.set( ServiceLocator.app.width * 0.5, ServiceLocator.app.height * 0.5 );
                        this._copiedUrlNotification.visible = true;
                        this._copiedUrlNotification.scale.set( 0 );
                        this._copiedUrlNotification.alpha = 0;
                        //
                        if ( this._copiedUrlNotificationTl )
                        {
                            this._copiedUrlNotificationTl.play( 0 );
                        }
                        else
                        {
                            this._copiedUrlNotificationTl = new gsap.core.Timeline();
                            this._copiedUrlNotificationTl.timeScale( 1.4 );
                            this._copiedUrlNotificationTl.to( this._copiedUrlNotification.scale, { x: 1, y: 1, duration: 0.4, ease: "back.out" }, 0 );
                            this._copiedUrlNotificationTl.to( this._copiedUrlNotification, { alpha: 1, duration: 0.1, ease: "sine.in" }, 0 );
                            this._copiedUrlNotificationTl.call( () => { 
                                this._root.interactiveChildren = true; 
                            }, null );
                            this._copiedUrlNotificationTl.to( this._copiedUrlNotification, { alpha: 0, duration: 0.6, ease: "sine.in" }, ">+0.2" );
                        }
                    } );
            }
        }

        private _onMultiplayer_PlayerPresenceUpdated( presenceEventPromise: Promise<LivePlayerDataEvent> ): void
        {
            presenceEventPromise
                .then( ( livePlayerDataEvent: LivePlayerDataEvent ) => {
                    if ( livePlayerDataEvent.action == LivePresenceAction.JOIN )
                    {
                        if ( livePlayerDataEvent.isHost )
                        {
                            Session.i.profile.uid = livePlayerDataEvent.uid;
                        }
                        else
                        {
                            if ( Session.i.room.length < Session.i.kGameRoomSize )
                            {
                                let profile: Profile = livePlayerDataEvent.profile;
                                profile.uid = livePlayerDataEvent.uid;
                                Session.i.room.push( livePlayerDataEvent.profile );

                                this._updateOtherPlayerSlots();

                                window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.PRIVATE_ROOM_ACCESS_GRANTED, uid: livePlayerDataEvent.uid, gameType: this._gameType } ) } );
                            }
                            else
                            {
                                window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.PRIVATE_ROOM_FULL, uid: livePlayerDataEvent.uid } ) } );
                            }
                        }
                    }
                    else if ( livePlayerDataEvent.action == LivePresenceAction.LEAVE )
                    {
                        if ( Session.i.profile.uid != livePlayerDataEvent.uid )
                        {
                            for ( let i: number = 0; i < Session.i.room.length; ++i )
                            {
                                if ( Session.i.room[ i ].uid == livePlayerDataEvent.uid )
                                {
                                    if ( this._isGameStarted )
                                    {
                                        Session.i.room[ i ].isBot = true;
                                    }
                                    else
                                    {
                                        Session.i.room.splice( i, 1 );
                                    }
                                    break;
                                }
                            }

                            if ( !this._isGameStarted )
                            {
                                this._startBtn.visible = Session.i.room.length >= 2;
                                if ( this._startBtn.visible )
                                {
                                    ServiceLocator.app.hidePrivateGameShareUrlDiv();
                                    
                                    this._footerText.text = Session.i.room[ 1 ].username.prefix + " just joined! Let's Play!"
                                }
                                else
                                {
                                    ServiceLocator.app.showPrivateGameShareUrlDiv( this._url );
                                    
                                    this._footerText.text = "";
                                }

                                this._updateOtherPlayerSlots();
                            }
                        }
                    }
                } );
        }

            private _updateOtherPlayerSlots(): void
            {
                for ( let i: number = 1; i < Session.i.kGameRoomSize; ++i )
                {
                    this._arrPlayerSlot[ i ].empty();
                }

                let myProfileIndex: number = 0;
                while ( Session.i.room[ myProfileIndex ].uid != Session.i.profile.uid )
                {
                    myProfileIndex += 1;
                }

                let playerCount: number = 1;
                let playerProfileIndex: number = myProfileIndex + 1;
                if ( playerProfileIndex > Session.i.room.length - 1 )
                {
                    playerProfileIndex = 0;
                }
                let playerSlotIndex: number = 1;
                //
                while ( playerCount < Session.i.room.length )
                {
                    this._arrPlayerSlot[ playerSlotIndex ].setPlayer( Session.i.room[ playerProfileIndex ] );

                    playerProfileIndex += 1;
                    if ( playerProfileIndex > Session.i.room.length - 1 )
                    {
                        playerProfileIndex = 0;
                    }
                    playerSlotIndex += 1;

                    playerCount += 1;
                }
            }

        private _onMultiplayer_EventsReceived( eventsPromise: Promise<LiveEventData[]> ): void
        {
            eventsPromise
                .then( ( arrData: LiveEventData[] ) => {
                    while ( arrData.length > 0 )
                    {
                        let data: LiveEventData = arrData.shift();
                        if ( data.payload === undefined )
                        {
                            Session.i.gameEvents.push( data );
                        }
                        else
                        {
                            let payload: any = null;
                            try
                            {
                                payload = JSON.parse( data.payload );
                            }
                            catch ( ex )
                            {
                                payload = null;
                            }
                            if ( payload !== null && payload.type === game_event_type.JOINED_PRIVATE_ROOM )
                            {
                                for ( let profile of Session.i.room )
                                {
                                    if ( profile.uid == payload.uid )
                                    {
                                        profile.isReady = true;
                                        profile.dartSkinIndex = payload.dartSkinIndex;
                                        break;
                                    }
                                }
                                if ( Session.i.room.length >= 2 )
                                {
                                    if ( this._isPlayAgain )
                                    {
                                        let isRoomReady: boolean = true;
                                        for ( let profile of Session.i.room )
                                        {
                                            if ( !profile.isReady )
                                            {
                                                isRoomReady = false;
                                                break;
                                            }
                                        }
                                        if ( isRoomReady )
                                        {
                                            this._startBtn.visible = true;
                                            ServiceLocator.app.hidePrivateGameShareUrlDiv();
                                            this._footerText.text = Session.i.room[ 1 ].username.prefix + " just joined! Let's Play!"
                                        }
                                    }
                                    else
                                    {
                                        this._startBtn.visible = true;
                                        ServiceLocator.app.hidePrivateGameShareUrlDiv();
                                        this._footerText.text = Session.i.room[ 1 ].username.prefix + " just joined! Let's Play!"
                                    }
                                }
                            }
                        }
                    }
                } );
        }

        private _onClassicModeBtn_Click(): void
        {
            this._gameType = game_type.CLASSIC_PRIVATE;

            this._classicModeBtn.interactive = false;
            this._pointsModeBtn.interactive = true;

            this._classicModeBtn.setTextures( { on: "button_mode_classic", pressed: "button_mode_classic_pressed" } );
            this._pointsModeBtn.setTextures( { on: "button_mode_301_off", pressed: "button_mode_301_pressed" } );
        
            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.PRIVATE_GAME_TYPE_UPDATED, gameType: this._gameType } ) } );
        }

        private _onPointsModeBtn_Click(): void
        {
            this._gameType = game_type.POINTS_PRIVATE;

            this._classicModeBtn.interactive = true;
            this._pointsModeBtn.interactive = false;

            this._classicModeBtn.setTextures( { on: "button_mode_classic_off", pressed: "button_mode_classic_pressed" } );
            this._pointsModeBtn.setTextures( { on: "button_mode_301", pressed: "button_mode_301_pressed" } );
        
            window.GameSnacks.liveMultiplayer.sendEvent( { payload: JSON.stringify( { type: game_event_type.PRIVATE_GAME_TYPE_UPDATED, gameType: this._gameType } ) } );
        }

        private _onStartBtn_Click(): void
        {
            this._classicModeBtn.interactive = false;
            this._pointsModeBtn.interactive = false;

            this._isGameStarted = true; 
            
            this._startBtn.visible = false;

            const kSeed: number = Math.floor( Math.random() * 9999 );
            let arrProfile: Array<Profile> = [];
            for ( let profile of Session.i.room )
            {
                arrProfile.push( profile );
            }
            if ( arrProfile.length < Session.i.kGameRoomSize )
            {
                this._arrPlayerSlot[ 3 ].visible = false;
                this._arrPlayerSlot[ 2 ].position.copyFrom( this._arrPlayerSlot[ 3 ].position );
            }
            //
            if ( this._isPlayAgain )
            {
                window.GameSnacks.liveMultiplayer.sendEvent( { 
                    payload: JSON.stringify( { 
                        type: game_event_type.PLAY_AGAIN_PRIVATE_GAME, 
                        uid: Session.i.profile.uid,
                        seed: kSeed,
                        room: arrProfile } ) 
                } );
            }
            else
            {
                window.GameSnacks.liveMultiplayer.sendEvent( { 
                    type: LiveEventDataType.START_GAME,
                    payload: JSON.stringify( { seed: kSeed, room: arrProfile } )
                } );
            }

            Session.i.prng = new PRNG( kSeed );

            this._backBtn.interactive = false;
            gsap.to( this._backBtn, { alpha: 0, duration: 0.4, ease: "linear.inOut" } );

            this._settings.interactive = false;
            gsap.to( this._settings, { alpha: 0, duration: 0.4, ease: "linear.inOut" } );

            this._gameStartedBg.visible = true;
            this._gameStartedBg.alpha = 0;
            //
            this._versusExplosion.visible = true;
            this._versusExplosion.scale.set( 1, 0 );
            //
            this._versus.visible = true;
            this._versus.scale.set( 0 );
            //
            this._gameReadyTl = new gsap.core.Timeline();
            this._gameReadyTl.to( this._gameStartedBg, { alpha: 1, duration: 1.6, ease: "sine.out" }, 0 );
            this._gameReadyTl.to( this._versusExplosion.scale, { y: 1, duration: 0.8, ease: "expo.out" }, 0 );
            this._gameReadyTl.to( this._versus.scale, { x: 1, y: 1, duration: 1, ease: "back.out" }, 0 );
            this._gameReadyTl.call( () => { this._onGameStarted.dispatch( this, this._gameType ); }, null, ">+2" );

            ServiceLocator.audio.playSfx( "matchmaking_found" );
            ServiceLocator.audio.stopMusic();
        }

        private _onProfile_Changed( profile: Profile): void
        {
            ( Session.i.profile.avatar as any ) = profile.avatar;
            ( Session.i.profile.username as any ) = profile.username;

            this._arrPlayerSlot[ 0 ].setPlayer( Session.i.profile )
        }

    public onLeave(): void
    {
        this._onGameStarted.dispose();
        this._onGameStarted = null;

        ServiceLocator.app.onCopyUrlBtnClick.remove( this._onCopyUrlBtn_Click, this );

        ServiceLocator.multiplayerEventManager.onPlayerPresenceUpdated.removeAll();
        ServiceLocator.multiplayerEventManager.onEventsReceived.removeAll();
        ServiceLocator.multiplayerEventManager.onProfileChange.remove( this._onProfile_Changed, this );

        ServiceLocator.app.hidePrivateGameShareUrlDiv();

        if ( this._gameReadyTl )
        {
            this._gameReadyTl.kill();
            this._gameReadyTl = null;
        }

        if ( this._copiedUrlNotificationTl )
        {
            this._copiedUrlNotificationTl.kill();
            this._copiedUrlNotificationTl = null;
        }

        this._settings.end();
        this._settings = null;

        super.onLeave();
    }

    public checkPrivateRoomReady(): void
    {
        if ( Session.i.room.length >= 2 )
        {
            let isRoomReady: boolean = true;
            for ( let profile of Session.i.room )
            {
                if ( !profile.isReady )
                {
                    isRoomReady = false;
                    break;
                }
            }
            if ( isRoomReady )
            {
                this._onStartBtn_Click();
            }
        }
    }

    // #endregion //
}